import React, { useEffect } from 'react';
import { Box, HStack } from '@chakra-ui/react';
import { Filter, FileText, Settings2, Download, ChevronDown, FileSpreadsheet, Radio, PenLine } from 'lucide-react';
import {
  ICatalogResponse,
  TABLE_MODE,
  TableMode,
  UNIT_MODE,
  UnitMode,
} from '../../../models/sessions';
import { IPeriod } from '../hooks/useCarichiTable';
import { installLeoStyles, leoTokens, btnInline } from '../theme/leoTokens';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomDatePicker from '../../../shared/CustomDatePicker/CustomDatePicker';
import CustomSelectInput from '../../../shared/CustomSelectInput/CustomSelectInput';

/* ──────────────────────────────────────────────────────────────────────────
 * CarichiToolbar — redesign
 * Layout del mockup:
 *   Row 1 · Tab bar con underline brand (Calendario / Tabella / Report)
 *   Row 2 · Segmented modalità + Period range + Segmented unità + preset chip
 *   Row 3 · Azioni (Filtri, Export PDF, Configura, Importa)
 * ────────────────────────────────────────────────────────────────────────── */

interface CarichiToolbarProps {
  catalog: ICatalogResponse | undefined;
  subview: 'calendar' | 'table' | 'report';
  setSubview: (s: 'calendar' | 'table' | 'report') => void;
  /** Elenco subview abilitate (feature-flag). Assente = tutte visibili. */
  visibleSubviews?: Array<'calendar' | 'table' | 'report'>;
  mode: TableMode;
  setMode: (m: TableMode) => void;
  unitMode: UnitMode;
  setUnitMode: (u: UnitMode) => void;
  presetId: string;
  setPresetId: (p: string) => void;
  period: IPeriod;
  setPeriod: (p: IPeriod) => void;
  kpiMetric: string;
  setKpiMetric: (k: string) => void;
  onOpenColumns: () => void;
  onOpenManualSession: () => void;
  /** Apre il wizard avanzato M6.2 B13 (ManualEntryWizard) — opzionale */
  onOpenManualWizard?: () => void;
  onOpenCsvWizard: () => void;
  onOpenGpsUpload: () => void;
  onOpenFilters: () => void;
  onExportPdf: () => void;
  activeFiltersCount: number;
}

const MODE_LABELS: Record<TableMode, string> = {
  [TABLE_MODE.DAY]: 'Giorno',
  [TABLE_MODE.WEEK_MATRIX]: 'Settimana',
  [TABLE_MODE.AGGREGATE]: 'Aggregato',
  [TABLE_MODE.PER_SESSION]: 'Per-sessione',
};

/** Converte stringa ISO "YYYY-MM-DD" → Date | null */
const isoToDate = (s: string): Date | null => {
  if (!s) return null;
  const d = new Date(s + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
};

/** Converte Date → stringa "YYYY-MM-DD" */
const dateToIso = (d: Date | null): string => {
  if (!d) return '';
  return d.toISOString().slice(0, 10);
};

export const CarichiToolbar: React.FC<CarichiToolbarProps> = ({
  catalog,
  subview,
  setSubview,
  visibleSubviews,
  mode,
  setMode,
  unitMode,
  setUnitMode,
  presetId,
  setPresetId,
  period,
  setPeriod,
  kpiMetric,
  setKpiMetric,
  onOpenColumns,
  onOpenManualSession,
  onOpenManualWizard,
  onOpenCsvWizard,
  onOpenGpsUpload,
  onOpenFilters,
  onExportPdf,
  activeFiltersCount,
}) => {
  useEffect(() => {
    installLeoStyles();
  }, []);

  const [importOpen, setImportOpen] = React.useState(false);
  const numericMetrics = (catalog?.metrics ?? []).filter(
    (m) => m.aggregation !== 'count' && m.id !== 'total_duration',
  );

  const kpiOptions = numericMetrics.map((m) => ({
    value: m.id,
    label: `${m.label} (${m.unit})`,
  }));

  return (
    <Box
      className="leo-scope"
      borderBottom="1px solid"
      borderColor={leoTokens.divider}
      bg="var(--c-card-bg)"
    >
      {/* Tab bar — i tab non inclusi in visibleSubviews (se definito) vengono nascosti */}
      <div className="leo-tabbar">
        {(!visibleSubviews || visibleSubviews.includes('calendar')) && (
          <button
            className={`leo-tab ${subview === 'calendar' ? 'is-active' : ''}`}
            onClick={() => setSubview('calendar')}
          >
            Calendario
          </button>
        )}
        {(!visibleSubviews || visibleSubviews.includes('table')) && (
          <button
            className={`leo-tab ${subview === 'table' ? 'is-active' : ''}`}
            onClick={() => setSubview('table')}
          >
            Tabella Squadra
          </button>
        )}
        {(!visibleSubviews || visibleSubviews.includes('report')) && (
          <button
            className={`leo-tab ${subview === 'report' ? 'is-active' : ''}`}
            onClick={() => setSubview('report')}
          >
            Report Atleta
          </button>
        )}
      </div>

      {/* Row 2 — mode + period + unit */}
      <HStack
        gap={3}
        px={4}
        py={3}
        flexWrap="wrap"
        borderBottom="1px solid"
        borderColor={leoTokens.rowDivider}
      >
        <div className="leo-segmented">
          {(Object.values(TABLE_MODE) as TableMode[]).map((m) => (
            <button
              key={m}
              className={`leo-seg ${mode === m ? 'is-active' : ''}`}
              onClick={() => setMode(m)}
            >
              {MODE_LABELS[m]}
            </button>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: leoTokens.muted,
          }}
        >
          <span style={{ fontWeight: 600 }}>Periodo</span>
          <CustomDatePicker
            date={isoToDate(period.start)}
            onChange={(d) => setPeriod({ ...period, start: dateToIso(d) })}
            placeholder="Inizio"
            showIcon
            dateFormat="dd/MM/yyyy"
          />
          <span>→</span>
          <CustomDatePicker
            date={isoToDate(period.end)}
            onChange={(d) => setPeriod({ ...period, end: dateToIso(d) })}
            placeholder="Fine"
            showIcon
            dateFormat="dd/MM/yyyy"
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: leoTokens.muted,
          }}
        >
          <span style={{ fontWeight: 600 }}>Unità</span>
          <div className="leo-segmented">
            <button
              className={`leo-seg ${
                unitMode === UNIT_MODE.ABSOLUTE ? 'is-active' : ''
              }`}
              onClick={() => setUnitMode(UNIT_MODE.ABSOLUTE)}
            >
              Assoluti
            </button>
            <button
              className={`leo-seg ${
                unitMode === UNIT_MODE.PER_MIN ? 'is-active' : ''
              }`}
              onClick={() => setUnitMode(UNIT_MODE.PER_MIN)}
            >
              /min
            </button>
          </div>
        </div>

        {mode === TABLE_MODE.WEEK_MATRIX && kpiOptions.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: leoTokens.muted }}>
              KPI
            </span>
            <div style={{ minWidth: 200 }}>
              <CustomSelectInput
                options={kpiOptions}
                value={kpiMetric}
                onChange={(val: string) => setKpiMetric(val)}
                placeholder="— seleziona KPI —"
                menuPosition="fixed"
              />
            </div>
          </div>
        )}
      </HStack>

      {/* Row 3 — preset chips + actions */}
      <HStack
        gap={3}
        px={4}
        py={3}
        flexWrap="wrap"
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          {(catalog?.systemPresets ?? []).map((p) => (
            <button
              key={p.id}
              className={`leo-chip ${presetId === p.id ? 'is-active' : ''}`}
              onClick={() => setPresetId(p.id)}
              title={p.description}
            >
              {p.name}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <CustomButton size="sm" color="outline" onClick={onOpenFilters} icon={<Filter size={12} />} iconPosition="left">
            {`Filtri${activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}`}
          </CustomButton>
          <CustomButton size="sm" color="outline" onClick={onExportPdf} icon={<FileText size={12} />} iconPosition="left">
            Export PDF
          </CustomButton>
          <CustomButton size="sm" color="outline" onClick={onOpenColumns} icon={<Settings2 size={12} />} iconPosition="left">
            Configura colonne
          </CustomButton>

          <div style={{ position: 'relative' }}>
            <CustomButton
              size="sm"
              color="primary"
              onClick={() => setImportOpen((v) => !v)}
              icon={<Download size={12} />}
              iconPosition="left"
            >
              {btnInline('Importa', <ChevronDown size={11} />)}
            </CustomButton>
            {importOpen && (
              <>
                <div
                  onClick={() => setImportOpen(false)}
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 100,
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    right: 0,
                    zIndex: 101,
                    background: 'var(--c-card-bg)',
                    border: `1px solid ${leoTokens.divider}`,
                    borderRadius: 8,
                    boxShadow: '0 10px 30px rgba(0,33,44,0.12)',
                    minWidth: 240,
                    padding: 6,
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setImportOpen(false);
                      onOpenCsvWizard();
                    }}
                    icon={<FileSpreadsheet size={15} />}
                    label="File CSV (wizard)"
                  />
                  <MenuItem
                    onClick={() => {
                      setImportOpen(false);
                      onOpenGpsUpload();
                    }}
                    icon={<Radio size={15} />}
                    label="File GPS (.fit/.gpx)"
                  />
                  <div
                    style={{
                      height: 1,
                      background: leoTokens.divider,
                      margin: '4px 0',
                    }}
                  />
                  <MenuItem
                    onClick={() => {
                      setImportOpen(false);
                      onOpenManualSession();
                    }}
                    icon={<PenLine size={15} />}
                    label="Dato manuale rapido"
                  />
                  {onOpenManualWizard && (
                    <MenuItem
                      onClick={() => {
                        setImportOpen(false);
                        onOpenManualWizard();
                      }}
                      icon={<PenLine size={15} />}
                      label="Input manuale avanzato (B13)"
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </HStack>
    </Box>
  );
};

const MenuItem: React.FC<{
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ onClick, icon, label }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      width: '100%',
      padding: '8px 10px',
      background: 'transparent',
      border: 'none',
      borderRadius: 6,
      fontSize: 13,
      color: leoTokens.ink,
      textAlign: 'left',
      cursor: 'pointer',
      fontFamily: 'inherit',
    }}
    onMouseEnter={(e) =>
      (e.currentTarget.style.background = leoTokens.bg2)
    }
    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
  >
    <span>{icon}</span>
    {label}
  </button>
);
